<template>
	<ui-modal-template>
		<template v-slot:title>
			<span>{{ __("Learn more about the price") }}</span>
		</template>

		<template v-slot:content>
			<ui-form class="h-full flex flex-col" :loading="submitting" @submit="submitForm">
				<div class="px-6 py-6">
					<h4 v-if="subheading" class="text-lg font-semibold text-gray-800 mb-2">
						{{ subheading }}
					</h4>
					<p class="text-gray-700">
						{{ __("Are you interested in selling at this price level? Simply submit the inquiry form! We will get back to you with further details and, if you wish, gladly forward your inquiry to the buyer.") }}
					</p>
				</div>

				<hr class="h-0.5 bg-gray-200 text-gray-200" />

				<div class="px-6 py-4 flex flex-col gap-y-4">
					<div class="flex">
						<ui-input-group
							class="basis-full sm:basis-[30.75%]"
							v-model="form.contact_name"
							field="contact_name"
							label="Name"
							:form="form"
							:required="true"
						></ui-input-group>
					</div>

					<div class="flex">
						<ui-input-group
							class="basis-full sm:basis-[30.75%]"
							v-model="form.qty_mt"
							field="qty_mt"
							type="text"
							label="Menge"
							suffix="t"
							:form="form"
							:required="true"
						></ui-input-group>
					</div>

					<div class="text-gray-700">
						<label class="flex font-medium">Kontaktweg</label>

						<div class="flex space-x-6 mt-1">
							<ui-radio-group
								v-for="method in contactMethods"
								:key="method.id"
								v-model="form.contact_method"
								:radioValue="method.id"
								:label="method.name"
								name="contact_method"
								field="contact_method"
								:form="form"
							></ui-radio-group>
						</div>
					</div>

					<div class="flex" v-if="form.contact_method === 'PHONE'">
						<ui-input-group
							class="basis-full sm:basis-[30.75%]"
							v-model="form.contact_phone"
							field="contact_phone"
							label="Telefonnummer"
							:form="form"
							:required="true"
						></ui-input-group>
					</div>

					<div class="flex" v-else-if="form.contact_method === 'EMAIL'">
						<ui-input-group
							class="basis-full sm:basis-[30.75%]"
							v-model="form.contact_email"
							field="contact_email"
							label="E-Mail-Adresse"
							:form="form"
							:required="true"
						></ui-input-group>
					</div>
				</div>

				<div class="bg-gray-100 p-6 mt-auto">
					<div class="flex justify-end">
						<ui-button
							:disabled="submitting"
							native-type="submit"
							label="Anfrage senden"
						/>
					</div>
				</div>
			</ui-form>
		</template>
	</ui-modal-template>
</template>

<script>
import axios from "axios";
import { dateToString, formatLocalizedNumber } from "../../cropOptions";

export default {
	name: "LocalPriceInquiryForm",
	props: {
		contactMethods: {
			type: Array,
			required: true,
		},
		contactName: {
			required: false,
			default: null,
		},
		contactEmail: {
			required: false,
			default: null,
		},
		contactPhone: {
			required: false,
			default: null,
		},
		localPriceId: {
			required: true,
		},
		// Der im Chart angezeigte Frei-Fuhre-Preis (CTA-Wert), der unverändert mitgesendet
		// und serverseitig übernommen wird — bewusst nicht neu berechnet.
		price: {
			required: true,
		},
		// Aus der referenzierten Zeile serverseitig abgeleitet (nur Anzeige der Unterüberschrift).
		productLabelKey: {
			required: false,
			default: null,
		},
		productHarvest: {
			required: false,
			default: null,
		},
		dateType: {
			required: false,
			default: null,
		},
		dateMonthFrom: {
			required: false,
			default: null,
		},
		dateMonthTo: {
			required: false,
			default: null,
		},
	},
	data() {
		return {
			// Schlanker Form-Stub: Werte + minimale Fehler-Schnittstelle, die die
			// Feld-Komponenten (InputGroup) erwarten. Fehleranzeige läuft über den
			// Bus-Pfad (form:invalid), nicht über den Inertia-Pfad — daher clearErrors als No-op.
			form: {
				contact_method: this.contactMethods[0]?.id ?? null,
				contact_name: this.contactName ?? "",
				contact_phone: this.contactPhone ?? "",
				contact_email: this.contactEmail ?? "",
				qty_mt: null,
				local_price_id: this.localPriceId,
				hasErrors: false,
				errors: {},
			},
			submitting: false,
		};
	},
	computed: {
		/**
		 * Unterüberschrift: "Tagesbestpreis für <Produkt> <Erntejahr>: <Preis> €/t (<Termin>)".
		 * Produkt/Erntejahr/Termin kommen aus dem dependencies-Endpoint, der Preis als CTA-Prop.
		 * Leer, solange die abgeleiteten Felder fehlen (dann kein Heading).
		 */
		subheading() {
			if (!this.productLabelKey) {
				return "";
			}

			return this.__("Best daily price for :product :harvest: :price €/t (:term)", {
				args: {
					product: this.__(this.productLabelKey),
					harvest: this.productHarvest,
					price: formatLocalizedNumber(this.price),
					term: dateToString(
						{
							date: {
								type: this.dateType,
								month_from: this.dateMonthFrom,
								month_to: this.dateMonthTo,
							},
						},
						"de",
						true
					),
				},
			});
		},
	},
	watch: {
		/**
		 * Beim Wechsel des Kontaktwegs das jeweils inaktive Feld leeren, damit keine
		 * verwaisten Eingaben (z.B. eine alte Telefonnummer) mitgesendet werden.
		 */
		"form.contact_method"(method) {
			if (method === "PHONE") {
				this.form.contact_email = "";
			} else if (method === "EMAIL") {
				this.form.contact_phone = "";
			}
		},
	},
	methods: {
		/**
		 * Anfrage absenden. Der angezeigte Frei-Fuhre-Preis (price_mt) wird
		 * unverändert mitgesendet und serverseitig übernommen; local_price_id bleibt die
		 * Referenz auf die Bestpreis-Zeile.
		 */
		submitForm() {
			this.submitting = true;

			axios
				.post(this.$route("local_price_inquiries.store"), {
					local_price_id: this.form.local_price_id,
					price_mt: this.price,
					qty_mt: this.form.qty_mt,
					contact_method: this.form.contact_method,
					contact_name: this.form.contact_name,
					contact_phone: this.form.contact_phone,
					contact_email: this.form.contact_email,
				})
				.then(this.onSuccess)
				.catch(this.onError)
				.finally(() => {
					this.submitting = false;
				});
		},

		/**
		 * Erfolg: Modal schließen und Bestätigung anzeigen.
		 */
		onSuccess() {
			this.$bus.$emit("modal:close");
			this.$bus.$emit("toast", {
				type: "success",
				message: "Vielen Dank! Wir melden uns bei Ihnen.",
			});
			this.$bus.$emit("local_price_inquiry:created");
		},

		/**
		 * Fehler: 422 als Feld-Validierung an den Bus, sonst generischer Modal-Fehler.
		 */
		onError(error) {
			if (error.response?.status === 422) {
				this.$bus.$emit("form:invalid", error.response.data.errors);

				return;
			}

			this.$bus.$emit("modal:error", error);
		},
	},
};
</script>
